import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { GoogleSheetsBaseService } from './google-sheets-base.service';

@Injectable({
  providedIn: 'root'
})
export class TimesheetSheetsService extends GoogleSheetsBaseService {

  /**
   * Gets communication data from a specific column in the sheet.
   * @param tabName - The name of the sheet tab
   * @param headerLetter - The column letter (e.g., 'A', 'B', 'C')
   * @returns An observable of the communication data
   */
  getCommunicationByColumn(tabName: string, headerLetter: string): Observable<any> {
    const startCell = headerLetter + '2';
    const endCell = headerLetter + '3';

    return this.getSheetData(tabName, startCell, endCell);
  }

  /**
   * Gets a list of fixed rate events from the timesheet sheet.
   * Returns an array of event objects with eventName, eventPrice, and completed properties.
   * @returns An observable of fixed rate events array
   */
  getFixedRateEvents(): Observable<any[]> {
    const tabName = 'timesheet';
    const startCell = 'H2';
    const endCell = 'H20';

    return this.getSheetData(tabName, startCell, endCell).pipe(
      map(response => {
        const fixedRateEventsArray = response?.[0] || [];
        const fixedRateEvents = [];

        fixedRateEventsArray.forEach((event: string) => {
          const fixedRateEventObject = {
            'eventName': event,
            'eventPrice': 0.0,
            'completed': false
          };
          fixedRateEvents.push(fixedRateEventObject);
        });

        return fixedRateEvents;
      })
    );
  }

  /**
   * Gets all timesheet statuses from the sheet.
   * Returns an array of status objects with value and label properties.
   * @returns An observable of timesheet statuses array
   */
  getAllTimesheetStatuses(): Observable<any[]> {
    const tabName = 'timesheet';
    const startCell = 'D2';
    const endCell = 'D10';

    return this.getSheetData(tabName, startCell, endCell).pipe(
      map(response => {
        const allStatusesArray = response?.[0] || [];
        const timesheetStatusesArray = [];

        allStatusesArray.forEach((status: string) => {
          const statusObject = {
            'value': status,
            'label': status
          };
          timesheetStatusesArray.push(statusObject);
        });

        return timesheetStatusesArray;
      })
    );
  }

  /**
   * Gets all timesheet types from the sheet.
   * Returns an array of type objects with value and label properties.
   * @returns An observable of timesheet types array
   */
  getAllTimesheetTypes(): Observable<any[]> {
    const tabName = 'timesheet';
    const startCell = 'A2';
    const endCell = 'A100';

    return this.getSheetData(tabName, startCell, endCell).pipe(
      map(response => {
        const allTypesArray = response?.[0] || [];
        const timesheetTypesArray = [];

        allTypesArray.forEach((type: string) => {
          const typeObject = {
            'value': type,
            'label': type
          };
          timesheetTypesArray.push(typeObject);
        });

        return timesheetTypesArray;
      })
    );
  }

  /**
   * Gets all timesheet frequencies from the sheet.
   * Returns an array of frequency objects with value and label properties.
   * @returns An observable of timesheet frequencies array
   */
  getAllTimesheetFrequencies(): Observable<any[]> {
    const tabName = 'timesheet';
    const startCell = 'B2';
    const endCell = 'B10';

    return this.getSheetData(tabName, startCell, endCell).pipe(
      map(response => {
        const allFrequenciesAsList = response?.[0] || [];
        const frequenciesArray = [];

        allFrequenciesAsList.forEach((frequency: string) => {
          const frequencyObject = {
            'value': frequency,
            'label': frequency
          };
          frequenciesArray.push(frequencyObject);
        });

        return frequenciesArray;
      })
    );
  }

  /**
   * Gets all timesheet cost center codes from the sheet.
   * Returns an array of cost center objects with value and label properties.
   * @returns An observable of cost center codes array
   */
  getAllTimesheetCostCenterCodes(): Observable<any[]> {
    const tabName = 'timesheet';
    const startCell = 'I2';
    const endCell = 'I300';

    return this.getSheetData(tabName, startCell, endCell).pipe(
      map(response => {
        const allCostCentersArray = response?.[0] || [];
        const costCentersForDropDownMenu = [];

        allCostCentersArray.forEach((costCenter: string) => {
          const costCenterObject = {
            'value': costCenter,
            'label': costCenter
          };
          costCentersForDropDownMenu.push(costCenterObject);
        });

        return costCentersForDropDownMenu;
      })
    );
  }

  /**
   * Gets all timesheet activities from the sheet.
   * Returns an array of activity strings.
   * @returns An observable of timesheet activities array
   */
  getAllTimesheetActivities(): Observable<string[]> {
    const tabName = 'timesheet';
    const startCell = 'C2';
    const endCell = 'C100';

    return this.getSheetData(tabName, startCell, endCell).pipe(
      map(response => response?.[0] || [])
    );
  }

  /**
   * Gets the PBJ job code list from the timesheet sheet.
   * Column J holds the code (number as string); column K holds the description.
   * Rows where the code is blank are dropped. Description text is taken as-is —
   * we do not parse a leading number out of it.
   */
  getPbjJobCodes(): Observable<{ code: string; description: string }[]> {
    const tabName = 'timesheet';
    const startCell = 'J2';
    const endCell = 'K100';

    return this.getSheetData(tabName, startCell, endCell).pipe(
      map(response => {
        const codeCol: string[] = response?.[0] ?? [];
        const descCol: string[] = response?.[1] ?? [];
        const out: { code: string; description: string }[] = [];
        for (let i = 0; i < codeCol.length; i++) {
          const code = (codeCol[i] ?? '').toString().trim();
          if (!code) continue;
          const description = (descCol[i] ?? '').toString().trim();
          out.push({ code, description });
        }
        return out;
      })
    );
  }
}
